<template>
  <div class="navarea-table-container">
    <!-- Фильтр для отображения только объектов по маршруту -->
    <div class="navarea-filter-controls mb-2">
      <b-form-checkbox 
        v-model="showOnlyRouteHazards" 
        @change="onFilterChange"
        :disabled="!hasRouteData"
      >
        Select Navarea along route
      </b-form-checkbox>
      <small v-if="!hasRouteData" class="text-muted ml-2">
        (No route data available)
      </small>
      <small v-else-if="showOnlyRouteHazards" class="text-info ml-2">
        Selected {{ filteredNavarea.length }} of {{ allNavareMessages.length }} items
      </small>
    </div>
    
    <b-table
        v-if="filteredNavarea.length"
        sticky-header
        size="sm"
        fixed
        striped
        hover
        responsive
        :items="formattedNavAreaData"
        :fields="fields"
        class="navarea-data-table"
        @row-clicked="handleRowClick"
        :tbody-tr-class="getRowClass"
        primary-key="uniqueKey"
    >
      <template #cell(content)="row">
        <div v-if="row.item.expanded">
          {{ row.item.content }}
          <b-button size="sm" variant="link" @click.stop="collapseRow(row.item.number)">
            less
          </b-button>
        </div>
        <div v-else>
          {{ truncateMessage(row.item.content) }}
          <b-button v-if="row.item.content.length > 100" size="sm" variant="link" @click.stop="expandRow(row.item.number)">
            more
          </b-button>
        </div>
      </template>
    </b-table>
    <div v-else-if="allNavareMessages.length" class="text-center text-muted py-3">
      No Navarea objects found along the route
    </div>
    <div v-else class="text-center text-muted py-3">
      No Navarea data available
    </div>
  </div>
</template>

<script>
import { useFleetStore } from '@/stores/fleet';
import { useRoutesStore } from '@/stores/routes';
import { mapState } from 'pinia';
import { eventBus } from '@/main';
import * as turf from '@turf/turf';

export default {
  name: "NavareaTable",  data() {
    return {
      expandedRowId: null,      
      selectedItemId: null,
      showOnlyRouteHazards: false,
      routeIntersectionCache: new Map(),
      isComponentActive: false,
      fields: [
        { key: 'number', label: 'Number', sortable: true, thStyle: { width: '25%' }  },
        { key: 'country', label: 'Country', sortable: true, thStyle: { width: '20%' } },
        { key: 'content', label: 'Message', sortable: true },
      ]
    }
  },  
  mounted() {
    console.log("111 - Navarea")
    // Помечаем компонент как активный
    this.isComponentActive = true;
    
    // Слушаем клики по Navarea объектам на карте
    eventBus.$on('highlight-navarea-item-in-table', this.highlightItem);  },// При активации компонента (если используется keep-alive) НЕ включаем Navarea слой автоматически
  
    // Слой должен включаться только при клике на таб
    activated() {
      this.isComponentActive = true;
    },
    // При деактивации компонента (если используется keep-alive) выключаем Navarea слой
    deactivated() {
      eventBus.$emit('toggle-navarea-layer', false);
      this.isComponentActive = false;
      
      // Отменяем pending debounce при деактивации
      if (this.cacheClearTimeout) {
        clearTimeout(this.cacheClearTimeout);
        this.cacheClearTimeout = null;
      }
    },  
  // Перед удалением компонента выключаем Navarea слой
  beforeDestroy() {
    eventBus.$emit('toggle-navarea-layer', false);
    this.isComponentActive = false;
    
    // Очищаем таймер при уничтожении компонента
    if (this.cacheClearTimeout) {
      clearTimeout(this.cacheClearTimeout);
      this.cacheClearTimeout = null;
    }
  },
  destroyed() {
    eventBus.$emit('toggle-navarea-layer', false);    // Отписываемся от событий по кликам
    eventBus.$off('highlight-navarea-item-in-table', this.highlightItem);
    
    // Очищаем таймеры
    if (this.calculationTimer) {
      clearTimeout(this.calculationTimer);
    }
    if (this.cacheClearTimeout) {
      clearTimeout(this.cacheClearTimeout);
    }
  },
  watch: {
    // Очищаем кэш при изменении маршрута, но только если это необходимо
    routeWaypoints: {
      handler() {
        // Очищаем кэш только если включена фильтрация и компонент активен (не скрыт в неактивной вкладке)
        if (this.showOnlyRouteHazards && this.isComponentActive) {
          // Добавляем debounce для избежания множественных очисток при быстрых изменениях маршрута
          this.debounceCacheClear();
        }
      },
      deep: true
    },
      // Очищаем кэш при изменении состояния фильтра
    showOnlyRouteHazards(newVal) {
      if (newVal && this.isComponentActive) {
        // Если включили фильтр - очищаем кэш чтобы пересчитать
        this.routeIntersectionCache.clear();
        this.startIntersectionCalculation();
      }
      // Если выключили фильтр - кэш не нужен, но очищать необязательно для производительности
    },
      // Отслеживаем изменения в фильтрованных данных для показа индикатора
    filteredNavarea() {
      // Если началась фильтрация и есть данные для расчета
      // Показываем индикатор только для больших наборов данных (>10 элементов)
      if (this.showOnlyRouteHazards && this.hasRouteData && this.isComponentActive && 
          this.navareaData.length > 10 && this.routeIntersectionCache.size === 0) {
        this.startIntersectionCalculation();
      }
    }
  },
  computed: {
    // Получаем данные Navarea
    ...mapState(useFleetStore, {
      navareaData: state => state.data.navarea || []
    }),
    // Получаем данные маршрута
    ...mapState(useRoutesStore, {
      routeWaypoints: state => state.getShipWaypoints || []
    }),

    // Преобразуем новый формат в плоский массив сообщений
    allNavareMessages() {
      console.log('🔍 NavareaTable: Raw navareaData:', this.navareaData);
      
      if (!this.navareaData) {
        console.log('❌ NavareaTable: navareaData is null/undefined');
        return [];
      }

      // Проверяем, пришли ли данные в новом формате (объект с results)
      if (this.navareaData.results && Array.isArray(this.navareaData.results)) {
        console.log('📊 NavareaTable: Data is in NEW API FORMAT (object with results), processing...');
        const messages = [];
        this.navareaData.results.forEach(countryData => {
          if (countryData.active_messages && countryData.active_messages.length > 0) {
            countryData.active_messages.forEach(message => {
              messages.push({
                id: message.id,
                number: message.number,
                content: message.content,
                country: countryData.country,
                type: countryData.type,
                time_upd: countryData.time_upd,
                coordinates: message.coordinates,
                uniqueKey: `${countryData.country}_${message.number}` // Уникальный ключ для таблицы
              });
            });
          }
        });
        console.log('✅ NavareaTable: Processed new API format data count:', messages.length);
        console.log('📋 NavareaTable: Sample new API format item:', messages[0]);
        return messages;
      }

      // Проверяем, если данные - это массив в старом формате
      if (Array.isArray(this.navareaData) && this.navareaData.length > 0 && this.navareaData[0].id1) {
        console.log('📊 NavareaTable: Data is in OLD FORMAT, converting...');
        const converted = this.navareaData.map((item, index) => ({
          ...item,
          // Конвертируем старые поля в новые для совместимости
          number: item.id1,
          content: item.message,
          coordinates: {
            decimal_coordinates: item.locationPoints || [],
            geojson_coordinates: item.locationAreas || []
          },
          uniqueKey: `${item.id1}_${item.area}_${index}` // Уникальный ключ для избежания дублирования
        }));
        console.log('✅ NavareaTable: Converted old format data count:', converted.length);
        console.log('📋 NavareaTable: Sample converted item:', converted[0]);
        return converted;
      }

      // Проверяем, если данные - это массив в новом формате (прямой массив стран)
      if (Array.isArray(this.navareaData)) {
        console.log('📊 NavareaTable: Data is in NEW FORMAT (array), processing...');
        const messages = [];
        this.navareaData.forEach(countryData => {
          if (countryData.active_messages && countryData.active_messages.length > 0) {
            countryData.active_messages.forEach(message => {
              messages.push({
                id: message.id,
                number: message.number,
                content: message.content,
                country: countryData.country,
                type: countryData.type,
                time_upd: countryData.time_upd,
                coordinates: message.coordinates,
                uniqueKey: `${countryData.country}_${message.number}` // Уникальный ключ для таблицы
              });
            });
          }
        });
        console.log('✅ NavareaTable: Processed new format data count:', messages.length);
        console.log('📋 NavareaTable: Sample new format item:', messages[0]);
        return messages;
      }

      console.log('❌ NavareaTable: Unknown data format:', typeof this.navareaData, this.navareaData);
      return [];
    },

    // Проверяем наличие данных маршрута
    hasRouteData() {
      return this.routeWaypoints && this.routeWaypoints.length > 0;
    },    
    // Получаем координаты маршрута для расчета пересечений
    routeCoordinates() {
      if (!this.hasRouteData) return [];
      
      return this.routeWaypoints.map(point => [
        parseFloat(point.lat),
        parseFloat(point.lon)
      ]).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
    },    
    // Вычисляем границы маршрута для быстрой предварительной проверки
    routeBounds() {
      if (!this.routeCoordinates || this.routeCoordinates.length === 0) {
        return null;
      }

      // Создаем простой хэш для маршрута
      const routeHash = this.routeCoordinates.map(coord => `${coord[0].toFixed(6)},${coord[1].toFixed(6)}`).join('|');
      
      // Если маршрут не изменился, возвращаем кэшированные границы
      if (this.lastRouteHash === routeHash && this.cachedRouteBounds) {
        return this.cachedRouteBounds;
      }
      
      let minLat = Infinity, maxLat = -Infinity;
      let minLon = Infinity, maxLon = -Infinity;
      
      this.routeCoordinates.forEach(([lat, lon]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      });
      
      // Добавляем небольшой буфер для учета погрешностей
      const buffer = 0.01; // ~1км
      const bounds = {
        minLat: minLat - buffer,
        maxLat: maxLat + buffer,
        minLon: minLon - buffer,
        maxLon: maxLon + buffer
      };

      // Кэшируем результат
      this.lastRouteHash = routeHash;
      this.cachedRouteBounds = bounds;
      
      return bounds;
    },

    routeBuffer() {
      if (!this.hasRouteData) {
        return null;
      }
      try {
        // Turf.js использует формат [lon, lat]
        const routeLine = turf.lineString(this.routeCoordinates.map(coord => [coord[1], coord[0]]));
        // Используем буфер в 10 километров, это стандартная ширина коридора безопасности для маршрутов.
        const buffer = turf.buffer(routeLine, 10, { units: 'kilometers' });
        return buffer;
      } catch (error) {
        console.error("Failed to create route buffer:", error);
        return null;
      }
    },

    // Фильтрованные данные по пересечению с маршрутом
    filteredNavarea() {
      // Если фильтр выключен или нет данных маршрута - возвращаем все
      if (!this.showOnlyRouteHazards || !this.hasRouteData) {
        return this.allNavareMessages;
      }

      if (!this.isComponentActive) {
        return this.allNavareMessages;
      }

      return this.allNavareMessages.filter(item => {
        return this.checkNavareaIntersectsRoute(item);
      });
    },

    formattedNavAreaData() {
      return this.filteredNavarea.map((item, index) => ({
        ...item,
        expanded: item.number === this.expandedRowId,
        uniqueKey: `${item.number}_${item.area}_${index}` // Уникальный ключ для избежания дублирования
      }));
    }
  },
  methods: {
    truncateMessage(message) {
      if (message && message.length > 100) {
        return message.substring(0, 100) + '...';
      }
      return message;
    },
    expandRow(id) {
      this.expandedRowId = id;
    },
    collapseRow() {
      this.expandedRowId = null;
    },
    // Обработчик клика по строкам таблицы
    handleRowClick(item) {
      this.selectedItemId = item.number;
      
      // Эмитим событие для показа объекта на карте
      eventBus.$emit('show-navarea-on-map', item);
      
      // Эмитим событие для выделения объекта на карте
      eventBus.$emit('highlight-navarea-on-map', item);
    },    // Выделение строки в таблице при клике по объекту на карте
    highlightItem(navareaItem) {
      const itemId = navareaItem.number || navareaItem.id || navareaItem;
      this.selectedItemId = itemId;
      
      // Найти элемент в фильтрованных данных
      const itemIndex = this.filteredNavarea.findIndex(item => item.number === itemId);
      if (itemIndex !== -1) {
        // Прокрутить таблицу к нужной строке
        this.$nextTick(() => {
          const table = this.$el.querySelector('.navarea-data-table');
          if (table) {
            const row = table.querySelector(`tbody tr:nth-child(${itemIndex + 1})`);
            if (row) {
              row.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }
        });
        
        // Выделяем соответствующие элементы на карте
        const selectedItem = this.filteredNavarea[itemIndex];
        eventBus.$emit('highlight-navarea-on-map', {
          number: selectedItem.number,
          area: selectedItem.area,
          content: selectedItem.content,
          coordinates: selectedItem.coordinates
        });
      } else {
        // Если элемент не найден, сбрасываем выделение
        eventBus.$emit('clear-navarea-highlight');
      }
    },

    // CSS класс для выбранной строки
    getRowClass(item, type) {
      if (type === 'row' && item && item.number === this.selectedItemId) {
        return 'table-info';
      }
      return '';
    },    // При изменении фильтра
    onFilterChange() {
      // Очищаем кэш только если включили фильтр и компонент активен
      if (this.showOnlyRouteHazards && this.isComponentActive) {
        this.routeIntersectionCache.clear();
      }
    },

    // Debounced очистка кэша для избежания множественных очисток при быстрых изменениях маршрута
    debounceCacheClear() {
      // Отменяем предыдущий таймер если он есть
      if (this.cacheClearTimeout) {
        clearTimeout(this.cacheClearTimeout);
      }
        // Устанавливаем новый таймер
      this.cacheClearTimeout = setTimeout(() => {
        this.routeIntersectionCache.clear();
        // Также очищаем кэш границ маршрута
        this.cachedRouteBounds = null;
        this.lastRouteHash = null;
        this.cacheClearTimeout = null;
      }, 300); // 300ms debounce - достаточно для избежания лишних вызовов
    },

    // Методы для управления индикатором расчета пересечений
    startIntersectionCalculation() {
      if (this.calculationTimer) {
        clearTimeout(this.calculationTimer);
      }
      
      if (!this.isCalculatingIntersections) {
        this.isCalculatingIntersections = true;
        eventBus.$emit('intersection-calculation-start');
      }
        // Задержка для завершения расчета (debounce)
      this.calculationTimer = setTimeout(() => {
        this.endIntersectionCalculation();
      }, 300);
    },
    
    endIntersectionCalculation() {
      if (this.isCalculatingIntersections) {
        this.isCalculatingIntersections = false;
        eventBus.$emit('intersection-calculation-end');
      }
      
      if (this.calculationTimer) {
        clearTimeout(this.calculationTimer);
        this.calculationTimer = null;
      }
    },

    // Быстрая проверка пересечения Bounding Box (AABB - Axis-Aligned Bounding Box)
    checkBoundingBoxIntersection(itemBounds, routeBounds) {
      if (!itemBounds || !routeBounds) return false;
      
      return !(itemBounds.maxLon < routeBounds.minLon || 
              itemBounds.minLon > routeBounds.maxLon ||
              itemBounds.maxLat < routeBounds.minLat || 
              itemBounds.minLat > routeBounds.maxLat);
    },

    // Получаем границы Navarea объекта
    getNavareaItemBounds(navareaItem) {
      if (!navareaItem.coordinates) return null;
      
      let minLat = Infinity, maxLat = -Infinity;
      let minLon = Infinity, maxLon = -Infinity;
      let hasValidCoords = false;

      // Получаем координаты из нового формата
      let coords = null;
      if (navareaItem.coordinates.decimal_coordinates) {
        coords = navareaItem.coordinates.decimal_coordinates;
      } else if (navareaItem.coordinates.geojson_coordinates) {
        coords = navareaItem.coordinates.geojson_coordinates;
      }

      if (!coords) return null;

      // Обрабатываем разные форматы координат
      if (Array.isArray(coords)) {
        coords.forEach(coord => {
          const coordArray = this.coordsToArray(coord);
          if (coordArray) {
            const [lon, lat] = coordArray;
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
            hasValidCoords = true;
          }
        });
      } else if (coords.lat !== undefined && coords.lon !== undefined) {
        const lat = parseFloat(coords.lat);
        const lon = parseFloat(coords.lon);
        if (!isNaN(lat) && !isNaN(lon)) {
          minLat = maxLat = lat;
          minLon = maxLon = lon;
          hasValidCoords = true;
        }
      }

      if (!hasValidCoords) return null;

      return {
        minLat,
        maxLat,
        minLon,
        maxLon
      };
    },
    
    // Проверка, пересекается ли Navarea объект с маршрутом
    checkNavareaIntersectsRoute(navareaItem) {
      if (!this.hasRouteData || !navareaItem.coordinates) {
        return false;
      }

      // Проверяем кэш
      const cacheKey = navareaItem.uniqueKey;
      if (this.routeIntersectionCache.has(cacheKey)) {
        return this.routeIntersectionCache.get(cacheKey);
      }

      try {
        // Создаем линию маршрута
        const routeCoordinates = this.routeWaypoints.map(wp => [wp.lon, wp.lat]);
        if (routeCoordinates.length < 2) return false;

        const routeLine = turf.lineString(routeCoordinates);
        const bufferedRoute = turf.buffer(routeLine, 10, { units: 'nauticalMiles' });

        let intersects = false;

        // Получаем координаты из доступных форматов
        let coords = null;
        if (navareaItem.coordinates.decimal_coordinates) {
          coords = navareaItem.coordinates.decimal_coordinates;
        } else if (navareaItem.coordinates.geojson_coordinates) {
          coords = navareaItem.coordinates.geojson_coordinates;
        }

        if (!coords) return false;

        // Обрабатываем разные форматы координат
        if (Array.isArray(coords)) {
          if (coords.length === 1) {
            // Одна точка
            const point = this.coordsToGeoJSON(coords[0]);
            if (point) {
              intersects = turf.booleanPointInPolygon(point, bufferedRoute);
            }
          } else if (coords.length > 1) {
            // Несколько точек - создаем полигон
            const geoCoords = coords.map(coord => this.coordsToArray(coord)).filter(c => c);
            if (geoCoords.length >= 3) {
              // Замыкаем полигон
              if (geoCoords[0][0] !== geoCoords[geoCoords.length - 1][0] || 
                geoCoords[0][1] !== geoCoords[geoCoords.length - 1][1]) {
                geoCoords.push(geoCoords[0]);
              }
              const polygon = turf.polygon([geoCoords]);
              intersects = turf.booleanOverlap(polygon, bufferedRoute) || 
                turf.booleanContains(bufferedRoute, polygon) || 
                turf.booleanContains(polygon, bufferedRoute);
            }
          }
        } else if (coords.lat !== undefined && coords.lon !== undefined) {
          // Объект с lat/lon
          const point = this.coordsToGeoJSON(coords);
          if (point) {
            intersects = turf.booleanPointInPolygon(point, bufferedRoute);
          }
        }

        // Сохраняем результат в кэш
        this.routeIntersectionCache.set(cacheKey, intersects);
        return intersects;

      } catch (error) {
        console.warn('Error checking navarea intersection:', error);
        return false;
      }
    },

    // Преобразование координат в GeoJSON точку
    coordsToGeoJSON(coords) {
      let lat, lon;
      
      if (coords.lat !== undefined && coords.lon !== undefined) {
        lat = parseFloat(coords.lat);
        lon = parseFloat(coords.lon);
      } else if (Array.isArray(coords) && coords.length >= 2) {
        lat = parseFloat(coords[1]);
        lon = parseFloat(coords[0]);
      } else {
        return null;
      }

      if (isNaN(lat) || isNaN(lon)) return null;
      
      return turf.point([lon, lat]);
    },

    // Преобразование координат в массив [lon, lat]
    coordsToArray(coords) {
      let lat, lon;
      
      if (coords.lat !== undefined && coords.lon !== undefined) {
        lat = parseFloat(coords.lat);
        lon = parseFloat(coords.lon);
      } else if (Array.isArray(coords) && coords.length >= 2) {
        lat = parseFloat(coords[1]);
        lon = parseFloat(coords[0]);
      } else {
        return null;
      }

      if (isNaN(lat) || isNaN(lon)) return null;
      
      return [lon, lat];
    },


    
    // Проверяет пересечение маршрута с полигоном через Turf.js
    checkRoutePolygonIntersection(routeCoords, polygonCoords) {
      if (!routeCoords || !polygonCoords || routeCoords.length < 2 || polygonCoords.length < 3) {
        return false;
      }

      try {
        // Убедимся, что координаты - валидные числа
        const validRouteCoords = routeCoords.filter(coord => 
          !isNaN(coord[0]) && !isNaN(coord[1]) && 
          Math.abs(coord[0]) <= 90 && Math.abs(coord[1]) <= 180
        );
        
        const validPolygonCoords = polygonCoords.filter(coord => 
          !isNaN(coord[0]) && !isNaN(coord[1]) && 
          Math.abs(coord[0]) <= 90 && Math.abs(coord[1]) <= 180
        );
        
        if (validRouteCoords.length < 2 || validPolygonCoords.length < 3) {
          return false;
        }

        // БЫСТРАЯ ПРЕДВАРИТЕЛЬНАЯ ПРОВЕРКА: Bounding Box для полигона
        const polygonBounds = this.getBoundsFromCoords(validPolygonCoords);
        if (!this.checkBoundingBoxIntersection(polygonBounds, this.routeBounds)) {
          return false;
        }
        
        // Создаем LineString из координат маршрута для Turf.js (lng, lat формат)
        const routeLine = turf.lineString(validRouteCoords.map(coord => [coord[1], coord[0]]));
        
        // Создаем Polygon из координат Navarea объекта для Turf.js и убеждаемся, что полигон замкнут
        const polygonForTurf = [...validPolygonCoords];
        if (polygonForTurf[0][0] !== polygonForTurf[polygonForTurf.length - 1][0] || 
          polygonForTurf[0][1] !== polygonForTurf[polygonForTurf.length - 1][1]) {
          polygonForTurf.push(polygonForTurf[0]); // Замыкаем полигон
        }
        
        // Создаем полигон и проверяем пересечение
        try {
          const navareaPolygon = turf.polygon([[...polygonForTurf.map(coord => [coord[1], coord[0]])]]);
          
          // Проверяем пересечение линии с полигоном
          return turf.booleanIntersects(routeLine, navareaPolygon);
        } catch (error) {
          // Возможно, полигон не валидный - пробуем создать буфер вокруг линии и проверить пересечение с точками
          const bufferedRoute = turf.buffer(routeLine, 0.01, { units: 'degrees' });
          
          // Проверяем, находится ли хотя бы одна точка полигона внутри буфера маршрута
          for (const coord of validPolygonCoords) {
            const point = turf.point([coord[1], coord[0]]);
            if (turf.booleanPointInPolygon(point, bufferedRoute)) {
              return true;
            }
          }
          
          // Если все не сработало, используем резервный метод
          return this.checkRoutePolygonIntersectionSimple(validRouteCoords, validPolygonCoords);
        }
      } catch (error) {
        // Fallback на простой алгоритм при ошибке в Turf.js
        return this.checkRoutePolygonIntersectionSimple(routeCoords, polygonCoords);
      }
    },

    // Вспомогательный метод для получения границ из массива координат
    getBoundsFromCoords(coords) {
      if (!coords || coords.length === 0) return null;
      
      let minLat = Infinity, maxLat = -Infinity;
      let minLon = Infinity, maxLon = -Infinity;
      
      coords.forEach(([lat, lon]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
      });
      
      return { minLat, maxLat, minLon, maxLon };
    },// Простой алгоритм проверки пересечения как fallback
    checkRoutePolygonIntersectionSimple(routeCoords, polygonCoords) {
      // Проверяем, находится ли хотя бы одна точка маршрута внутри полигона
      for (const point of routeCoords) {
        if (this.pointInPolygon(point, polygonCoords)) {
          return true;
        }
      }
      
      // Проверяем пересечение каждого сегмента маршрута с полигоном
      for (let i = 0; i < routeCoords.length - 1; i++) {
        const segmentStart = routeCoords[i];
        const segmentEnd = routeCoords[i + 1];
        
        // Проверяем, пересекает ли сегмент маршрута полигон
        if (this.lineIntersectsPolygon(segmentStart, segmentEnd, polygonCoords)) {
          return true;
        }
      }
      
      // Проверяем, находится ли хотя бы одна точка полигона внутри буфера маршрута
      // (для случаев, когда полигон находится целиком внутри маршрута)
      if (this.routeBuffer) {
        for (const point of polygonCoords) {
          const turfPoint = turf.point([point[1], point[0]]);
          if (turf.booleanPointInPolygon(turfPoint, this.routeBuffer)) {
            return true;
          }
        }
      }

      return false;
    },

    // Проверяем пересечение линии с полигоном
    lineIntersectsPolygon(lineStart, lineEnd, polygonCoords) {
      // Проверяем, находятся ли концы линии внутри полигона
      if (this.pointInPolygon(lineStart, polygonCoords) || 
        this.pointInPolygon(lineEnd, polygonCoords)) {
        return true;
      }

      // Проверяем пересечение линии с ребрами полигона
      for (let i = 0; i < polygonCoords.length; i++) {
        const edgeStart = polygonCoords[i];
        const edgeEnd = polygonCoords[(i + 1) % polygonCoords.length];
        
        if (this.linesIntersect(lineStart, lineEnd, edgeStart, edgeEnd)) {
          return true;
        }
      }

      return false;
    },

    // Проверяет, находится ли точка внутри полигона (алгоритм ray casting)
    pointInPolygon(point, polygonCoords) {
      const [x, y] = point;
      let inside = false;

      for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
        const [xi, yi] = polygonCoords[i];
        const [xj, yj] = polygonCoords[j];

        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
          inside = !inside;
        }
      }

      return inside;
    },

    // Проверяем пересечение двух линий
    linesIntersect(line1Start, line1End, line2Start, line2End) {
      const [x1, y1] = line1Start;
      const [x2, y2] = line1End;
      const [x3, y3] = line2Start;
      const [x4, y4] = line2End;

      const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      
      if (Math.abs(denominator) < 1e-10) {
        return false; // Линии параллельны
      }

      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denominator;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denominator;
      
      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    }
  }
}
</script>

<style scoped>
.navarea-table-container {
  width: 100%;
  padding: 0px;
  font-size: 0.9rem;
}

.navarea-filter-controls {
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-bottom: 8px;
}

.navarea-filter-controls .form-check {
  margin-bottom: 0;
}

.section-title {
  font-size: 1rem;
  margin-bottom: 5px;
  color: #555;
}

.navarea-data-table {
  max-height: 400px;
  overflow-y: auto;
  font-size: 0.85rem;
}

.no-data {
  color: #888;
  font-style: italic;
  font-size: 0.85rem;
  margin-top: 5px;
}

/* Стиль для строк таблицы */
:deep(.table) tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

:deep(.table) tr:hover {
  background-color: #f0f8ff;
}

/* Выделенная строка */
:deep(.table-info) {
  background-color: #d1ecf1 !important;
}

/* Стили для мобильной версии */
@media (max-width: 768px) {
  .navarea-table-container {
    padding: 0px;
  }

  .section-title {
    font-size: 0.9rem;
  }

  .navarea-data-table {
    font-size: 0.8rem;
  }
}

:deep(tr.highlighted-row) {
  background-color: #ffee88 !important;
  transition: background-color 0.3s;
}
</style>